import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import Chip from 'core-system/Chip'
import palette from 'core-system/Themes/palette'

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.white};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const IconContainer = styled.div<{ bgColor: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ProgramInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const ProgramDescription = styled(Text)`
  color: ${(props) => props.theme.palette.text.placeholder};
  font-size: 0.8125rem;
`

const ServicesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
`

const ServicePill = styled.div<{ hasLogo?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: ${(props) =>
    props.hasLogo ? '0.375rem 0.75rem' : '0.375rem 0.75rem'};
  background-color: ${(props) => props.theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey.grey400};
  border-radius: 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${(props) => props.theme.palette.text.primary};
`

const ServiceLogo = styled.img`
  height: 1rem;
  width: auto;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

// Pre-defined icons for different program types
const DollarIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
    <text
      x='12'
      y='17'
      textAnchor='middle'
      fill={palette.success.green600}
      fontSize='16'
      fontWeight='bold'
    >
      $
    </text>
  </svg>
)

const BikeIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <circle
      cx='5'
      cy='17'
      r='3'
      stroke={palette.text.placeholder}
      strokeWidth='2'
    />
    <circle
      cx='19'
      cy='17'
      r='3'
      stroke={palette.text.placeholder}
      strokeWidth='2'
    />
    <path
      d='M12 17L9 9H15L12 17Z'
      stroke={palette.text.placeholder}
      strokeWidth='2'
      strokeLinejoin='round'
    />
  </svg>
)

const CarIcon = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M7 17H4V10L6 4H18L20 10V17H17M7 17H17M7 17V14H17V17'
      stroke={palette.error.red500}
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <circle cx='7' cy='17' r='2' fill={palette.error.red500} />
    <circle cx='17' cy='17' r='2' fill={palette.error.red500} />
  </svg>
)

export type ProgramTagType = 'PRE-TAX' | 'EMPLOYER-PAID' | 'ACTIVE'

export interface ServiceItem {
  type: 'text' | 'logo'
  label: string
  logoUrl?: string
}

interface EnrolledProgramRowProps {
  name: string
  description: string
  tagType: ProgramTagType
  iconType: 'pretax' | 'micromobility' | 'grh' | 'flex'
  services?: ServiceItem[]
  onManage: () => void
}

const getIconConfig = (iconType: string) => {
  switch (iconType) {
    case 'pretax':
      return {
        bgColor: palette.success.green100,
        icon: <DollarIcon />,
      }
    case 'micromobility':
      return {
        bgColor: palette.grey.grey200,
        icon: <BikeIcon />,
      }
    case 'grh':
      return {
        bgColor: palette.error.red100,
        icon: <CarIcon />,
      }
    case 'flex':
    default:
      return {
        bgColor: palette.secondary.purple100,
        icon: <DollarIcon />,
      }
  }
}

const EnrolledProgramRow: React.FC<EnrolledProgramRowProps> = ({
  name,
  description,
  tagType,
  iconType,
  services,
  onManage,
}) => {
  const iconConfig = getIconConfig(iconType)
  const chipVariant = tagType === 'PRE-TAX' ? 'red' : 'blue'

  return (
    <RowContainer>
      <LeftSection>
        <IconContainer bgColor={iconConfig.bgColor}>
          {iconConfig.icon}
        </IconContainer>
        <ProgramInfo>
          <Text variant='action4'>{name}</Text>
          <ProgramDescription variant='body2'>{description}</ProgramDescription>
          {services && services.length > 0 && (
            <ServicesRow>
              {services.map((service, idx) => (
                <ServicePill key={idx} hasLogo={service.type === 'logo'}>
                  {service.type === 'logo' && service.logoUrl && (
                    <ServiceLogo src={service.logoUrl} alt={service.label} />
                  )}
                  {service.label}
                </ServicePill>
              ))}
            </ServicesRow>
          )}
        </ProgramInfo>
      </LeftSection>
      <RightSection>
        <Chip variant={chipVariant} width='auto'>
          {tagType}
        </Chip>
        <Button variant='secondary' size='small' onClick={onManage}>
          Manage
        </Button>
      </RightSection>
    </RowContainer>
  )
}

export default React.memo(EnrolledProgramRow)
